import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Sparkles } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface WishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lotteryName: string;
  onSubmit: (number: number) => Promise<void>;
  isLoading?: boolean;
}

export const WishDialog = ({ open, onOpenChange, lotteryName, onSubmit, isLoading }: WishDialogProps) => {
  const [number, setNumber] = useState("");

  const handleSubmit = async () => {
    if (!number) {
      toast({
        title: "Missing Information",
        description: "Please enter your lottery number.",
        variant: "destructive",
      });
      return;
    }

    const num = parseInt(number, 10);
    if (isNaN(num) || num <= 0) {
      toast({
        title: "Invalid Number",
        description: "Please enter a valid positive number.",
        variant: "destructive",
      });
      return;
    }

    try {
      await onSubmit(num);
      setNumber("");
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="h-6 w-6 text-accent" />
            {lotteryName}
          </DialogTitle>
          <DialogDescription>
            Enter your lottery number. It will be encrypted before submission.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="number" className="flex items-center gap-2">
              Your Lottery Number
              <Lock className="h-3 w-3 text-muted-foreground" />
            </Label>
            <Input
              id="number"
              type="number"
              min="1"
              placeholder="Enter a positive number (e.g., 1, 2, 3...)"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Your number will be encrypted and kept private until the draw
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
            disabled={isLoading}
          >
            {isLoading ? "Submitting..." : "Submit Number"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
